import { useTranslation } from 'react-i18next';
import { useContext, useMemo, useState } from 'react';
import { Box, Grid } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {
  Button, Card, CardContent, Typography,
} from 'ovComponents';
import { additionalInfo, FieldRequiredType } from 'ovComponents/4-module/configurableOptionFields';
import InfoDialog from 'ovComponents/2-component/infoDialog/infoDialog';
import { getBackendLanguage, translateBackend } from 'assets/i18n/config';
import SecuritiesSelectionTable, { Security } from 'ovComponents/3-pattern/securitiesSelectionTable/securitiesSelectionTable';
import { PieChartSecurities } from 'ovComponents/3-pattern/pieChartSecurities/pieChartSecurities';
import { ConfirmationModal, ConfirmButtonColors, CancelButtonColors } from 'ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { ValidateSecuritiesDialogBox } from './components/validateSecuritiesDialogBox';
import { AnalyticsContext } from '../../../../../providers/analyticsProvider';
import { ovAnalyticsEvents } from '../../../../../util/analytics/analytics';
import { ModelPortfolioData } from '../createModelPortfolio/createModelPortfolio';
import { WorkflowCompletionForm } from '../../components/workflowCompletionForm';

export const AddModelPortfolioSecuritiesVisual = ({
  options,
  workflowCompletion,
  securities = [],
  continueFunc,
  onAddSecurity,
  onUpdateSecurity,
  onUpdateTaxRank,
  onUpdateTargetPercentage,
  onRemoveSecurity,
  onSaveForLater,
  onImportFromCsv,
  onReorderSecurities,
  modelPortfolioDetails,
  loading,
}: {
  options: any;
  workflowCompletion?: any;
  securities: Security[];
  continueFunc: () => void;
  onAddSecurity: () => void;
  onUpdateSecurity: (index: number, security: Security) => void;
  onRemoveSecurity: (index: number) => void;
  onUpdateTaxRank: (index: number, value: number) => void;
  onUpdateTargetPercentage: (index: number, value: number) => void;
  onSaveForLater: () => void;
  onImportFromCsv: () => void;
  onReorderSecurities: (securities: Security[]) => void;
  modelPortfolioDetails: ModelPortfolioData;
  loading: boolean;
}) => {
  const { t } = useTranslation(['modelPortfolios', 'shared']);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTemplateValidationDialogOpen, setIsTemplateValidationDialogOpen] = useState(false);
  const [utilizeRemainingAllocation, setUtilizeRemainingAllocation] = useState(false);
  const { portfolioTemplate } = useMemo(() => modelPortfolioDetails, [modelPortfolioDetails]);

  const getRoundedTotalPercentage = () => Number(securities.reduce((sum, security) => sum + (security.percentage || 0), 0).toFixed(2));

  const validate = () => {
    const fields = [];
    if (options?.showSecurityAllocationTable?.required === FieldRequiredType.ERROR && securities.length === 0) {
      fields.push('securities');
    }
    return fields.length === 0;
  };

  const checkTemplateCompliance = () => {
    if (!portfolioTemplate?.assetClassBranches) {
      return true;
    }

    const securitiesAssetClassMap = securities.reduce((acc: Record<string, number>, security) => {
      const assetClass = security.financialProduct.primaryAssetClass?.translatedName?.[getBackendLanguage()]
                        || security.financialProduct.primaryAssetClass?.name
                        || 'Other';
      acc[assetClass] = (acc[assetClass] || 0) + (security.percentage || 0);
      return acc;
    }, {});

    const hasMismatch = portfolioTemplate.assetClassBranches.some((branch) => {
      const assetClassName = branch.assetClass?.name || 'Other';
      const targetPercentage = branch.targetPercentage || 0;
      const actualPercentage = securitiesAssetClassMap[assetClassName] || 0;

      return Math.abs(targetPercentage - actualPercentage) > 0.01;
    });

    return !hasMismatch;
  };

  const submit = async () => {
    if (validate()) {
      const totalPercentage = getRoundedTotalPercentage();
      if (totalPercentage !== 100) {
        setIsDialogOpen(true);
        return;
      }

      // Check template compliance
      if (!checkTemplateCompliance()) {
        setIsTemplateValidationDialogOpen(true);
        return;
      }

      await handleContinue();
    }
  };

  const adjustSecuritiesForCashAndEquivalents = () => {
    if (!utilizeRemainingAllocation) {
      return;
    }

    const totalPercentage = getRoundedTotalPercentage();
    const remainingPercentage = 100 - totalPercentage;

    if (remainingPercentage <= 0) {
      return;
    }

    if (modelPortfolioDetails.zeroPercentCash) {
      // Case 1: If zeroPercentCash is true, find existing cash equivalent and add remaining to it
      const cashEquivalentSecurity = securities.find((security) => security.isCashEquivalent);
      if (cashEquivalentSecurity) {
        const updatedSecurities = securities.map((security) => {
          if (security.isCashEquivalent) {
            return {
              ...security,
              percentage: (security.percentage || 0) + remainingPercentage,
            };
          }
          return security;
        });
        onReorderSecurities(updatedSecurities);
      }
    } else {
      // Case 2: If zeroPercentCash is false, find or create cash security
      const existingCashSecurity = securities.find((security) => security.financialProduct.isCash);

      if (existingCashSecurity) {
        const updatedSecurities = securities.map((security) => {
          if (security.financialProduct.isCash) {
            return {
              ...security,
              percentage: (security.percentage || 0) + remainingPercentage,
            };
          }
          return security;
        });
        onReorderSecurities(updatedSecurities);
      } else {
        const seenAssetClasses = new Set<string>();
        const orderedAssetClasses = securities.reduce<string[]>((acc, sec) => {
          const assetClass = sec.financialProduct.primaryAssetClass?.translatedName?.[getBackendLanguage()] || sec.financialProduct.primaryAssetClass?.name;
          if (assetClass && !seenAssetClasses.has(assetClass)) {
            seenAssetClasses.add(assetClass);
            acc.push(assetClass);
          }
          return acc;
        }, []);

        if (!seenAssetClasses.has('Cash')) {
          orderedAssetClasses.push('Cash');
        }

        const colorList = ['supportOne', 'supportTwo', 'supportThree', 'supportFour', 'supportFive', 'supportSix'];
        const newColorMap = orderedAssetClasses.reduce<Record<string, 'supportOne' | 'supportTwo' | 'supportThree' | 'supportFour' | 'supportFive' | 'supportSix'>>((acc, assetClass, i) => ({
          ...acc,
          [assetClass]: colorList[i % colorList.length] as 'supportOne' | 'supportTwo' | 'supportThree' | 'supportFour' | 'supportFive' | 'supportSix',
        }), {});

        const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newCashSecurity: Security = {
          financialProduct: {
            id: tempId,
            name: 'Cash',
            isCash: true,
            primaryAssetClass: {
              name: 'Cash',
              id: tempId,
              translatedName: {
                en: 'Cash',
                fr: 'Cash',
              },
            },
            translatedName: {
              en: 'Cash',
              fr: 'Cash',
            },
          },
          percentage: remainingPercentage,
          isCashEquivalent: false,
          taxRank: securities.length > 0 ? Math.max(...securities.map((security) => security.taxRank ?? 0)) + 1 : 1,
          id: tempId,
          color: newColorMap.Cash,
        };

        const updatedSecurities = [...securities, newCashSecurity];
        onReorderSecurities(updatedSecurities);
      }
    }

    setUtilizeRemainingAllocation(false);
  };

  const handleContinue = () => {
    sendAnalytic(ovAnalyticsEvents.workflowsAddModelPortfolioDetailsContinueButtonSelect, {
      workflowStepTitle: options?.title,
      workflowId: workflowCompletion?.workflow?.id,
      workflowName: workflowCompletion?.workflow?.name,
      activeWorkflowCompletionId: workflowCompletion?.id,
      objectId: workflowCompletion?.objectId,
      objectType: workflowCompletion?.objectType,
    });
    continueFunc();
  };

  const isDisabled = () => {
    if (!securities.length) {
      return true;
    }

    const hasInvalidSecurity = securities.some((security) => {
      if (!security.financialProduct?.id
          || (security.financialProduct.id.toString().startsWith('temp-') && !security.financialProduct.isCash)
          || !security.financialProduct.primaryAssetClass) {
        return true;
      }

      return false;
    });

    if (hasInvalidSecurity) {
      return true;
    }

    return getRoundedTotalPercentage() > 100;
  };

  return (
    <Box sx={{ ...options?.componentProps, mt: 3, mr: -30 }}>
      <WorkflowCompletionForm
        onSubmit={submit}
        submitButtonProps={{
          label: t('shared:continue'),
          disabled: isDisabled(),
          dataTestId: 'add-model-portfolio-securities-continue-button',
        }}
        buttonFooter={

          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="flex-end"
            flex={1}>
            {options?.buttonSaveForLater?.enabled && (
              <Button
                sx={{ mr: 2, textAlign: 'center' }}
                data-testid="add-model-portfolio-securities-save-for-later-button"
                variant="tonal"
                onClick={onSaveForLater}
                color="primary"
                label={translateBackend(options?.buttonSaveForLater?.label)}
                disabled={isDisabled()}
                loading={loading}
              />
            )}
          </Box>
        }
      >
        {options?.title && (
          <Typography variant="displayLarge" sx={{
            mt: 1,
            mb: 2,
            fontWeight: 400,
            fontSize: '40px',
            fontFamily: 'Tiempos Text',
          }}>
            {translateBackend(options?.title)}
            {additionalInfo(options?.title?.additionalInfo) && (
              <InfoDialog information={additionalInfo(options?.title?.additionalInfo) ?? ''} />
            )}
          </Typography>
        )}

        <Grid container spacing={2}>
          {options?.securityAllocationChart?.enabled && (
            <Grid item xs={12} md={3.5}>
              <Card variant='outlined'>
                <CardContent>
                  <PieChartSecurities
                    testId="pie-chart-securities"
                    securities={securities}
                    modelPortfolioName={modelPortfolioDetails?.translatedName?.[getBackendLanguage()] ?? ''}
                    portfolioTemplate={portfolioTemplate}
                  />
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid item xs={12} md={8.5}>
            <Card variant='outlined'>
              {options?.showSecurityAllocationTable?.enabled && (
                <>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                      <InfoOutlinedIcon fontSize='small' data-testid={'info-taxrank-edit-sercurity-icon'} />
                      <Typography
                        variant="bodyMedium"
                        data-testid={'info-taxrank-edit-sercurity-text'}
                        sx={{ fontWeight: 400, whiteSpace: 'nowrap' }}
                      >
                        {t('modelPortfolios:portfolio.securitiesOrderedByTaxRank')}
                      </Typography>
                    </Box>
                  </CardContent>
                  <SecuritiesSelectionTable
                    securities={securities}
                    options={options}
                    onRemoveSecurity={(index: number) => onRemoveSecurity(index)}
                    onUpdateSecurity={(index: number, security: Security) => onUpdateSecurity(index, security)}
                    onAddSecurity={onAddSecurity}
                    onUpdateTaxRank={(index: number, value: number) => onUpdateTaxRank(index, value)}
                    onUpdateTargetPercentage={(index: number, value: number) => onUpdateTargetPercentage(index, value)}
                    onReorderSecurities={(data: any[]) => onReorderSecurities(data)}
                  />
                </>
              )}
            </Card>
          </Grid>
        </Grid>
      </WorkflowCompletionForm>

      {options?.validateSecurities?.enabled && (
        <ValidateSecuritiesDialogBox
          open={isDialogOpen}
          onClose={() => {
            setIsDialogOpen(false);
            setUtilizeRemainingAllocation(false);
          }}
          title={translateBackend(options?.validateSecurities?.title)}
          bodyText={translateBackend(options?.validateSecurities?.bodyText)}
          checkboxLabel={translateBackend(options?.validateSecurities?.checkboxLabel)}
          onCheckboxChange={(checked) => setUtilizeRemainingAllocation(checked)}
          confirmButtonText={t('shared:confirm')}
          cancelButtonText={t('shared:cancel')}
          onConfirm={() => {
            setIsDialogOpen(false);
            adjustSecuritiesForCashAndEquivalents();
          }}
          isChecked={utilizeRemainingAllocation}
        />
      )}

      <ConfirmationModal
        open={isTemplateValidationDialogOpen}
        onConfirm={() => {
          setIsTemplateValidationDialogOpen(false);
          handleContinue();
        }}
        onCancel={() => setIsTemplateValidationDialogOpen(false)}
        title={t('modelPortfolios:templateDoesntMatch.title')}
        bodyText={t('modelPortfolios:templateDoesntMatch.body')}
        confirmButtonLabel={t('shared:continue')}
        cancelButtonLabel={t('shared:cancel')}
        confirmButtonColor={ConfirmButtonColors.primary}
        cancelButtonColor={CancelButtonColors.primary}
        testIdPrefix="template-validation-dialog"
      />
    </Box>
  );
};

export default AddModelPortfolioSecuritiesVisual;
